import type { ThemeConfig } from 'antd'

import {
  darkBrandPrimary,
  darkPalette,
  darkSemanticSeed,
  lightPalette
} from './palette'
import { brandSeed } from './seed'

export const themeTokens: NonNullable<ThemeConfig['token']> = {
  // Brand + semantic seeds — single source of truth in `./seed.ts`, ported
  // from ebs-platform-web's `@shared/theme`. Only the seeds are set here; AntD
  // derives the bg/hover/active shades (incl. colorLink*) from colorPrimary so
  // org-level theme overrides (settings.token from the API) cascade through the
  // whole palette. The same seed feeds the `--brand-teal*` CSS-var layer.
  ...brandSeed,

  colorLink: brandSeed.colorPrimary,

  // Clean selected/active tints. AntD's derived colorPrimaryBg is a muddy grey
  // for a dark seed like #0F766E (generate()[0] = #a8b5b2), which is why
  // selected menu/dropdown/table rows looked ugly. Overriding these alias
  // tokens with a color-mix into white gives a clean tint for ANY primary.
  colorPrimaryBg: 'color-mix(in srgb, var(--ant-color-primary) 12%, #fff)',
  colorPrimaryBgHover: 'color-mix(in srgb, var(--ant-color-primary) 18%, #fff)',
  controlItemBgActive: lightPalette.selected,
  controlItemBgActiveHover: lightPalette.selectedHover,

  // Soft success wash (status pills, "done" chips). AntD derives this from the
  // #22C55E seed as #f0fff3 — near-white, so the pill read as grey rather than
  // green. Pinned to the design-system tint.
  colorSuccessBg: '#DCFCE7',
  colorSuccessText: lightPalette.successText,
  colorWarningText: lightPalette.warningText,
  colorErrorText: lightPalette.errorText,
  colorErrorTextActive: lightPalette.errorTextActive,

  // Foreground / text
  colorTextBase: '#0B1420',
  colorText: '#0B1420',
  colorTextSecondary: '#3F4A5A',
  colorTextTertiary: '#5B6675',
  colorTextQuaternary: '#8A95A4',
  colorTextDisabled: '#B5BCC5',
  colorTextPlaceholder: '#8A95A4',

  // Surfaces
  colorBgBase: '#FFFFFF',
  colorBgLayout: '#F4F6F8',
  colorBgContainer: '#FFFFFF',
  colorBgElevated: '#FFFFFF',
  colorBgSpotlight: '#0B1420',
  colorBgMask: 'rgba(11,20,32,0.45)',

  // Borders & splits
  colorBorder: '#D7DCE2',
  colorBorderSecondary: '#E8ECF1',
  colorSplit: '#EAEEF2',

  // Fills (translucent)
  colorFill: 'rgba(11,20,32,0.05)',
  colorFillSecondary: 'rgba(11,20,32,0.035)',
  colorFillTertiary: 'rgba(11,20,32,0.02)',
  colorFillQuaternary: 'rgba(11,20,32,0.012)',

  // Type — Inter (UI), IBM Plex Serif (display), JetBrains Mono (numbers/ids)
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyCode:
    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13, // body
  fontSizeSM: 12,
  fontSizeLG: 15,
  fontSizeXL: 16,
  fontSizeIcon: 12,
  fontSizeHeading1: 28, // serif via component override
  fontSizeHeading2: 24,
  fontSizeHeading3: 20,
  fontSizeHeading4: 18,
  fontSizeHeading5: 16,
  lineHeight: 1.618,
  lineHeightSM: 1.45,
  lineHeightLG: 1.618,

  // Radius — 4-step scale (matches --r-* in colors_and_type.css)
  borderRadius: 6,
  borderRadiusSM: 4,
  borderRadiusXS: 3,
  borderRadiusLG: 8,
  borderRadiusOuter: 6,

  // Density — compact controls (XS 20 · SM 24 · MD 28 · LG 32)
  // Matches the --h-* tokens in colors_and_type.css.
  controlHeight: 28,
  controlHeightSM: 24,
  controlHeightLG: 32,
  controlHeightXS: 20,
  paddingContentHorizontal: 12,
  paddingContentVertical: 8,
  padding: 14,
  paddingLG: 18,
  paddingMD: 16,
  paddingSM: 10,
  paddingXS: 8,
  paddingXXS: 4,
  margin: 14,
  marginLG: 18,
  marginMD: 16,
  marginSM: 10,
  marginXS: 8,
  marginXXS: 4,

  // Motion + shadow
  motionDurationFast: '0.12s',
  motionDurationMid: '0.16s',
  motionDurationSlow: '0.24s',
  motionEaseOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  motionEaseInOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',

  boxShadow: '0 1px 2px rgba(11,20,32,0.04), 0 1px 3px rgba(11,20,32,0.05)',
  boxShadowSecondary:
    '0 4px 14px rgba(11,20,32,0.07), 0 1px 3px rgba(11,20,32,0.04)',
  boxShadowTertiary: '0 1px 2px rgba(11,20,32,0.04)',

  wireframe: false
}

const ink = (alpha: number) => `rgba(0,0,0,${alpha})`
const glow = (alpha: number) => `rgba(255,255,255,${alpha})`

export const darkThemeTokens: NonNullable<ThemeConfig['token']> = {
  ...themeTokens,

  ...darkSemanticSeed,
  colorPrimary: darkBrandPrimary,
  colorLink: darkBrandPrimary,
  colorLinkHover: 'var(--ant-color-primary-hover)',
  colorLinkActive: 'var(--ant-color-primary-active)',
  colorPrimaryBg: `color-mix(in srgb, var(--ant-color-primary) 10%, ${darkPalette.container})`,
  colorPrimaryBgHover: `color-mix(in srgb, var(--ant-color-primary) 16%, ${darkPalette.container})`,
  controlItemBgActive: darkPalette.selected,
  controlItemBgActiveHover: darkPalette.selectedHover,
  colorSuccessBg: darkPalette.successBg,
  colorSuccessText: darkPalette.successText,
  colorWarningText: darkPalette.warningText,
  colorErrorText: darkPalette.errorText,
  colorErrorTextActive: darkPalette.errorTextActive,

  colorTextBase: darkPalette.text,
  colorText: darkPalette.text,
  colorTextSecondary: darkPalette.textSecondary,
  colorTextTertiary: darkPalette.textTertiary,
  colorTextQuaternary: darkPalette.textQuaternary,
  colorTextDisabled: darkPalette.textDisabled,
  colorTextPlaceholder: darkPalette.textQuaternary,

  colorBgBase: darkPalette.layout,
  colorBgLayout: darkPalette.layout,
  colorBgContainer: darkPalette.container,
  colorBgElevated: darkPalette.elevated,
  colorBgSpotlight: darkPalette.spotlight,
  colorBgMask: ink(0.6),

  colorBorder: darkPalette.border,
  colorBorderSecondary: darkPalette.borderSecondary,
  colorSplit: darkPalette.split,

  colorFill: glow(0.1),
  colorFillSecondary: glow(0.07),
  colorFillTertiary: glow(0.045),
  colorFillQuaternary: glow(0.025),

  boxShadow: `0 1px 2px ${ink(0.4)}, 0 1px 3px ${ink(0.3)}`,
  boxShadowSecondary: `0 6px 18px ${ink(0.5)}, 0 1px 3px ${ink(0.4)}`,
  boxShadowTertiary: `0 1px 2px ${ink(0.4)}`
}
